import { useCallback, useMemo } from "react"

import { useMatch } from "react-router-dom"

import { MapWithPhotoViewer } from "@l2r-front/l2r-common"
import { useImageCollectionDispatchContext, useImageCollectionStateContext } from "@l2r-front/l2r-images"
import { ReactMapGl, useMapContext } from "@l2r-front/l2r-map"
import { useIsFetchingNetwork, useNetworksStateContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"

import { useIsFetchingRiskZones } from "../../../modules/riskZones/hooks/queries/useIsFetchingRiskZones"
import { useIsFetchingSpeedings } from "../../../modules/speedings/hooks/queries/useIsFetchingSpeedings"
import { appSafety } from "../../constants/appSafety"

import { SafetyMapLegend } from "../SafetyMapLegend"

export function Map(props) {

    const {
        children,
        ...mapProps
    } = props

    const { getSelectedNetworkBoundingBox } = useNetworksStateContext()
    const [mapState, mapDispatch] = useMapContext()
    const { setMapBoundingBoxToFit } = mapDispatch
    const { imageLayerDisplayed } = useImageCollectionStateContext()
    const { onOpenImageViewer, onSwitchImageLayer, isImageCollectionAvailable } = useImageCollectionDispatchContext()
    const isFetchingNetwork = useIsFetchingNetwork()
    const isFetchingRiskZones = useIsFetchingRiskZones()
    const isFetchingSpeedings = useIsFetchingSpeedings()
    const isFetching = isFetchingRiskZones || isFetchingSpeedings || isFetchingNetwork
    const isMobile = useIsMobileDevice()

    const { error } = mapState

    const mapInitialViewState = useMemo(() => {
        const [minLng, minLat, maxLng, maxLat] = getSelectedNetworkBoundingBox()
        return {
            longitude: (minLng + maxLng) / 2,
            latitude: (minLat + maxLat) / 2,
            zoom: 8,
        }
    }, [getSelectedNetworkBoundingBox])

    const onRecenterOnNetworkBoundingBox = useCallback(() => {
        const [minLng, minLat, maxLng, maxLat] = getSelectedNetworkBoundingBox()
        setMapBoundingBoxToFit({
            minLng,
            minLat,
            maxLng,
            maxLat,
        })
    }, [getSelectedNetworkBoundingBox, setMapBoundingBoxToFit])

    const onSwitchImageLayerCallback = isImageCollectionAvailable() ? onSwitchImageLayer : undefined
    const match = useMatch("/:networkId/:modules/:subModule/*")
    const subModule = match?.params.subModule
    const module = Object.values(appSafety.modules).find(m => m.path === subModule)?.id

    return (
        <MapWithPhotoViewer
            dragRotate={false}
            error={error}
            imageLayerDisplayed={imageLayerDisplayed}
            initialViewState={mapInitialViewState}
            isBaseMap
            isLoading={isFetching}
            module={module}
            onOpenImageViewer={onOpenImageViewer}
            onSwitchImageLayer={onSwitchImageLayerCallback}
            onRecenter={onRecenterOnNetworkBoundingBox}
            topLeftElements={!isMobile && [<SafetyMapLegend key="safety-map-legend" />]}
            topRightElements={isMobile && [<SafetyMapLegend key="safety-map-legend" />]}
            {...mapProps}
        >
            {children}
        </MapWithPhotoViewer>
    )
}

Map.propTypes = {
    ...ReactMapGl.propTypes,
    className: PropTypes.string,
}